import React, { useEffect, useMemo, useState } from 'react';
import CardShelf from './CardShelf';
import { getEvolutionLine, hasWord } from '../../utils/evolutionLine';
import { sortListingsByRarity } from '../../utils/sortListings';

const API = process.env.REACT_APP_API_BASE_URL;

const isPokemon = (l) => (l?.cert?.card?.supertype || '').toLowerCase().startsWith('pok');
const cardName = (l) => l?.cert?.card?.name || l?.cert?.name || '';

// Under a search for a Pokémon, one shelf per relative in its evolution line,
// "Evolves To" first, then "Evolves From", closest relative first in each
// ("Evolves To: Charizard", "Evolves From: Charmander"),
// each with that Pokémon's listings. Relatives with no listings are skipped,
// and cards already in the main results aren't repeated.
const EvolutionShelves = ({ term, excludeListings = [] }) => {
    const [shelves, setShelves] = useState([]);

    useEffect(() => {
        const controller = new AbortController();
        setShelves([]);

        const load = async () => {
            try {
                const line = await getEvolutionLine(term, controller.signal);
                const relatives = [
                    ...line.to.map((name) => ({ name, label: `Evolves Into ${name}` })),
                    ...line.from.map((name) => ({ name, label: `Evolves From ${name}` })),
                ];
                if (relatives.length === 0) return;

                const withListings = await Promise.all(relatives.map(async (r) => {
                    const p = new URLSearchParams({ cardName: r.name, count: '200' });
                    const res = await fetch(`${API}/listings?${p}`, { signal: controller.signal });
                    const data = res.ok ? await res.json() : {};
                    const listings = (data.listings || []).filter((l) => isPokemon(l) && hasWord(cardName(l), r.name));
                    return { ...r, listings: sortListingsByRarity(listings) };
                }));
                setShelves(withListings);
            } catch (e) {
                if (e.name !== 'AbortError') setShelves([]); // optional extra; fail quietly
            }
        };

        load();
        return () => controller.abort();
    }, [term]);

    const excludeIds = useMemo(() => new Set(excludeListings.map((l) => l.id)), [excludeListings]);

    const visible = shelves
        .map((s) => ({ ...s, listings: s.listings.filter((l) => !excludeIds.has(l.id)) }))
        .filter((s) => s.listings.length > 0);

    if (visible.length === 0) return null;

    return (
        <div className="gh-evolutions">
            {visible.map((s) => (
                <CardShelf
                    key={s.label}
                    title={s.label}
                    seeAllTo={`/listings/${encodeURIComponent(s.name)}`}
                    listings={s.listings}
                    limit={Infinity}
                />
            ))}
        </div>
    );
};

export default EvolutionShelves;
